import { highlightProductUri, parseHighlightProductUri } from '@highlight/common/product'
import type { BriefLink, BriefBullet, BriefLinkPreview, MeetingBriefStructuredContent } from './types'

/**
 * Mirror of the backend's `normalizeBriefLinkUrl`: Highlight product URIs
 * collapse to their canonical form, everything else is already canonical.
 * Both sides of the lookup must normalize the same way, because the backend
 * dedupes the relevant-links list by this key.
 */
export function normalizeBriefLinkUrl(url: string): string {
  const reference = parseHighlightProductUri(url)
  return reference ? highlightProductUri(reference) : url
}

/**
 * Index a v2 brief's references by the link URL they produced, so a link pill
 * can show where the record came from and why the brief cited it.
 *
 * Several references can resolve to one URL, such as a Slack thread cited by
 * two items. They merge into one entry, first non-null metadata wins and the
 * support lines union, because one pill gets one card.
 */
export function buildBriefLinkPreviews(content: MeetingBriefStructuredContent): Record<string, BriefLinkPreview> {
  const supportByReference = new Map<string, Array<string>>()
  for (const item of content.brief.items) {
    for (const referenceId of item.references) {
      const support = supportByReference.get(referenceId) ?? []
      support.push(item.support)
      supportByReference.set(referenceId, support)
    }
  }

  const previews: Record<string, BriefLinkPreview> = {}
  for (const [referenceId, reference] of Object.entries(content.references)) {
    if (!reference.uri) continue
    const key = normalizeBriefLinkUrl(reference.uri)
    const support = supportByReference.get(referenceId) ?? []
    const existing = previews[key]
    previews[key] = existing
      ? {
          source: existing.source,
          title: existing.title ?? reference.title,
          contentTime: existing.contentTime ?? reference.contentTime,
          support: dedupe([...existing.support, ...support]),
        }
      : {
          source: reference.source,
          title: reference.title,
          contentTime: reference.contentTime,
          support: dedupe(support),
        }
  }
  return previews
}

function dedupe(values: Array<string>): Array<string> {
  return [...new Set(values.map((value) => value.trim()).filter(Boolean))]
}

/**
 * Pair each brief bullet with the links it cites, restoring the association the
 * flat markdown body throws away.
 *
 * Resolved against the relevant-links list rather than by rebuilding links from
 * the references: the backend already decided which references are navigable
 * (`briefLinkFromEvidence` drops calendar rows, non-HTTP URIs, uncitable product
 * types), and re-deriving that here would be a second copy of those rules that
 * could disagree. A reference the backend rejected simply finds no link and
 * contributes no chip.
 */
export function buildBriefBullets(content: MeetingBriefStructuredContent, links: Array<BriefLink>): Array<BriefBullet> {
  const linkByUrl = new Map(links.map((link) => [normalizeBriefLinkUrl(link.url), link]))

  return content.brief.items.map((item) => {
    const cited = new Map<string, BriefLink>()
    for (const referenceId of item.references) {
      const uri = content.references[referenceId]?.uri
      if (!uri) continue
      const key = normalizeBriefLinkUrl(uri)
      const link = linkByUrl.get(key)
      // Insertion order is citation order, and the map keeps the first of a
      // bullet citing one record twice.
      if (link && !cited.has(key)) cited.set(key, link)
    }
    return { text: item.text, support: item.support, links: [...cited.values()] }
  })
}
